/**
 * IndexedDB Manager für Offline-Unterstützung
 * Verwaltet lokale Speicherung von Submissions, Tickets, Kunden
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface OfflineSubmission {
  id: string;
  formType: 'ONSITE_SERVICE' | 'ONSITE_DELIVERY';
  status: 'DRAFT' | 'PENDING_SYNC' | 'SYNCED';
  data: Record<string, any>;
  syncedAt?: number;
  lastModified: number;
  createdAt: number;
}

interface OfflineTicket {
  id: number;
  cwTicketId: string;
  company?: { id: number; name: string };
  priority?: { id: number; name: string };
  status?: { id: number; name: string };
  summary: string;
  lastModified: number;
}

interface OfflineCustomer {
  id: number;
  name: string;
  address?: string;
  lastModified: number;
}

interface PortalDB extends DBSchema {
  submissions: {
    key: string;
    value: OfflineSubmission;
    indexes: { 'by-status': string; 'by-formType': string };
  };
  tickets: {
    key: number;
    value: OfflineTicket;
  };
  customers: {
    key: number;
    value: OfflineCustomer;
  };
}

class OfflineDatabase {
  private db: IDBPDatabase<PortalDB> | null = null;

  async init(): Promise<void> {
    this.db = await openDB<PortalDB>('portal-offline', 1, {
      upgrade(db: IDBPDatabase<PortalDB>) {
        // Submissions Store
        if (!db.objectStoreNames.contains('submissions')) {
          const submissionStore = db.createObjectStore('submissions', { keyPath: 'id' });
          submissionStore.createIndex('by-status', 'status');
          submissionStore.createIndex('by-formType', 'formType');
        }

        // Tickets Store
        if (!db.objectStoreNames.contains('tickets')) {
          db.createObjectStore('tickets', { keyPath: 'id' });
        }

        // Customers Store
        if (!db.objectStoreNames.contains('customers')) {
          db.createObjectStore('customers', { keyPath: 'id' });
        }
      },
    });
  }

  private ensureInit(): IDBPDatabase<PortalDB> {
    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }
    return this.db;
  }

  // ===== Submissions =====

  async saveSubmission(submission: OfflineSubmission): Promise<void> {
    const db = this.ensureInit();
    await db.put('submissions', submission);
  }

  async getSubmission(id: string): Promise<OfflineSubmission | undefined> {
    const db = this.ensureInit();
    return db.get('submissions', id);
  }

  async getPendingSyncSubmissions(): Promise<OfflineSubmission[]> {
    const db = this.ensureInit();
    return db.getAllFromIndex('submissions', 'by-status', 'PENDING_SYNC');
  }

  async getAllSubmissions(): Promise<OfflineSubmission[]> {
    const db = this.ensureInit();
    return db.getAll('submissions');
  }

  async updateSubmissionStatus(id: string, status: OfflineSubmission['status']): Promise<void> {
    const db = this.ensureInit();
    const submission = await db.get('submissions', id);
    if (submission) {
      submission.status = status;
      submission.lastModified = Date.now();
      if (status === 'SYNCED') {
        submission.syncedAt = Date.now();
      }
      await db.put('submissions', submission);
    }
  }

  async deleteSubmission(id: string): Promise<void> {
    const db = this.ensureInit();
    await db.delete('submissions', id);
  }

  // ===== Tickets =====

  async saveTickets(tickets: OfflineTicket[]): Promise<void> {
    const db = this.ensureInit();
    const tx = db.transaction('tickets', 'readwrite');
    for (const ticket of tickets) {
      await tx.store.put(ticket);
    }
    await tx.done;
  }

  async getTicket(id: number): Promise<OfflineTicket | undefined> {
    const db = this.ensureInit();
    return db.get('tickets', id);
  }

  async getAllTickets(): Promise<OfflineTicket[]> {
    const db = this.ensureInit();
    return db.getAll('tickets');
  }

  async clearTickets(): Promise<void> {
    const db = this.ensureInit();
    await db.clear('tickets');
  }

  // ===== Customers =====

  async saveCustomers(customers: OfflineCustomer[]): Promise<void> {
    const db = this.ensureInit();
    const tx = db.transaction('customers', 'readwrite');
    for (const customer of customers) {
      await tx.store.put(customer);
    }
    await tx.done;
  }

  async getCustomer(id: number): Promise<OfflineCustomer | undefined> {
    const db = this.ensureInit();
    return db.get('customers', id);
  }

  async getAllCustomers(): Promise<OfflineCustomer[]> {
    const db = this.ensureInit();
    return db.getAll('customers');
  }

  async clearCustomers(): Promise<void> {
    const db = this.ensureInit();
    await db.clear('customers');
  }

  // ===== General =====

  async getStorageInfo(): Promise<{ submissions: number; tickets: number; customers: number }> {
    const db = this.ensureInit();
    return {
      submissions: await db.count('submissions'),
      tickets: await db.count('tickets'),
      customers: await db.count('customers'),
    };
  }

  async clear(): Promise<void> {
    const db = this.ensureInit();
    await db.clear('submissions');
    await db.clear('tickets');
    await db.clear('customers');
  }
}

// Singleton instance
let dbInstance: OfflineDatabase | null = null;

export function getOfflineDB(): OfflineDatabase {
  if (!dbInstance) {
    dbInstance = new OfflineDatabase();
  }
  return dbInstance;
}

export async function initOfflineDB(): Promise<void> {
  const db = getOfflineDB();
  await db.init();
}

export type { OfflineSubmission, OfflineTicket, OfflineCustomer };
